import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

import { StorageKey } from '@/common/utils/constants';
import storage from '@/common/utils/storage';
import {
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '@react-starter/shared/components/shadcn/dropdown-menu';
import { Button } from '@react-starter/shared/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@react-starter/shared/components/shadcn/tooltip';

/**
 * The `LanguageToggle` component renders a `Dropdown` which allows users
 * to select the language in which they wish to view the application.
 */
export const LanguageToggle = () => {
  const { i18n, t } = useTranslation();

  /**
   * Set the application-wide langague code used for i18n.
   * @param {string} lng - A langage code, e.g. `en` or `es`.
   */
  const setLanguage = (lng: string) => {
    storage.setItem(StorageKey.Language, lng);
    i18n.changeLanguage(lng);
  };

  return (
    <DropdownMenu data-testid="dropdown-language">
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="cursor-pointer"
              aria-label={t('select-language')}
              data-testid="button-language-menu-trigger"
            >
              <Languages data-testid="icon-language" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('select-language')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-32" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuGroup>
          <DropdownMenuLabel>Languages</DropdownMenuLabel>
          <DropdownMenuItem className="cursor-pointer" onClick={() => setLanguage('en')} data-testid="dropdown-item-en">
            English
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => setLanguage('fr')} data-testid="dropdown-item-fr">
            French
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => setLanguage('es')} data-testid="dropdown-item-es">
            Spanish
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
